import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, GitMerge, Zap, ArrowRight, Loader2, ChevronDown, ChevronUp, Circle, Link2, Bell, GitBranch, Database, Layers, Search } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import MobileHeader from '../layout/MobileHeader';
import { workflowService, DAGDefinition, DAGNode, DAGEdge } from '../services/WorkflowService';
import { useTranslation } from 'react-i18next';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  Handle,
  Position,
  MarkerType,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { formatTriggerFilter } from '../utils/triggerFilters';

// ----- helpers ---------------------------------------------------------------

const NODE_TYPE_LABELS: Record<string, string> = {
  run_integration:   'Run Integration',
  send_notification: 'Send Notification',
  condition:         'Condition',
  entity_action:     'Entity Action',
  foreach_array:     'For Each',
  run_python:        'Run Script',
  search_parts:      'Search Parts',
};

const TRIGGER_TYPE_LABELS: Record<string, string> = {
  order_status_changed: 'Order status changed',
  item_added_to_cart:   'Item added to cart',
  search_performed:     'Search performed',
  order_created:        'Order created',
  contact_created:      'Contact created',
};

function triggerLabel(type?: string) {
  if (!type) return 'Unknown trigger';
  return TRIGGER_TYPE_LABELS[type] ?? type.replace(/_/g, ' ');
}

function nodeLabel(n: DAGNode) {
  return n.label ?? NODE_TYPE_LABELS[n.type] ?? n.type;
}

// ----- DAG diagram -----------------------------------------------------------

const NODE_COLORS: Record<string, string> = {
  run_integration:   'bg-blue-50 border-blue-300 text-blue-800',
  send_notification: 'bg-purple-50 border-purple-300 text-purple-800',
  condition:         'bg-amber-50 border-amber-300 text-amber-800',
  entity_action:     'bg-teal-50 border-teal-300 text-teal-800',
  foreach_array:     'bg-orange-50 border-orange-300 text-orange-800',
  run_python:        'bg-violet-50 border-violet-300 text-violet-800',
  search_parts:      'bg-sky-50 border-sky-300 text-sky-800',
};

const NODE_ICON_MAP: Record<string, React.ElementType> = {
  run_integration:   Link2,
  send_notification: Bell,
  condition:         GitBranch,
  entity_action:     Database,
  foreach_array:     Layers,
  run_python:        Circle,
  search_parts:      Search,
};

interface DAGNodeData extends Record<string, unknown> {
  nodeType: string;
  label: string;
}
type RFDAGNode = Node<DAGNodeData>;

const WorkflowNodeComponent = ({ data, selected }: NodeProps & { data: DAGNodeData }) => {
  const colorClass = NODE_COLORS[data.nodeType] ?? 'bg-gray-50 border-gray-300 text-gray-800';
  const Icon = NODE_ICON_MAP[data.nodeType] ?? Circle;
  return (
    <div className={`rounded-xl border-2 px-4 py-3 min-w-[160px] max-w-[220px] shadow-sm ${colorClass} ${selected ? 'ring-2 ring-offset-1 ring-gray-900' : ''}`}>
      <Handle type="target" position={Position.Top} className="!w-2 !h-2 !bg-gray-400" />
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="text-xs font-semibold truncate">{data.label || data.nodeType}</span>
      </div>
      <Handle type="source" position={Position.Bottom} className="!w-2 !h-2 !bg-gray-400" />
    </div>
  );
};

const rfNodeTypes = { dagNode: WorkflowNodeComponent };

// Auto-layout: topological sort → assign depth layers → centre per layer
function computeLayout(
  nodes: DAGNode[],
  edges: DAGEdge[],
  nodeW = 200,
  nodeH = 60,
  xGap = 60,
  yGap = 80,
): Record<string, { x: number; y: number }> {
  // Build adjacency + in-degree maps
  const inDegree: Record<string, number> = {};
  const children: Record<string, string[]> = {};
  nodes.forEach(n => { inDegree[n.id] = 0; children[n.id] = []; });
  edges.forEach(e => {
    if (children[e.source]) children[e.source].push(e.target);
    if (e.target in inDegree) inDegree[e.target]++;
  });

  // Kahn's BFS → assign layer depth
  const depth: Record<string, number> = {};
  const queue: string[] = nodes.filter(n => inDegree[n.id] === 0).map(n => n.id);
  queue.forEach(id => { depth[id] = 0; });

  let head = 0;
  while (head < queue.length) {
    const cur = queue[head++];
    (children[cur] ?? []).forEach(child => {
      depth[child] = Math.max(depth[child] ?? 0, (depth[cur] ?? 0) + 1);
      inDegree[child]--;
      if (inDegree[child] === 0) queue.push(child);
    });
  }
  // Fallback depth for any node not reached
  nodes.forEach(n => { if (depth[n.id] === undefined) depth[n.id] = 0; });

  // Group nodes by layer
  const layers: Record<number, string[]> = {};
  nodes.forEach(n => {
    const d = depth[n.id];
    if (!layers[d]) layers[d] = [];
    layers[d].push(n.id);
  });

  const positions: Record<string, { x: number; y: number }> = {};
  Object.entries(layers).forEach(([layerStr, ids]) => {
    const layer = Number(layerStr);
    const totalW = ids.length * nodeW + (ids.length - 1) * xGap;
    const startX = -totalW / 2;
    ids.forEach((id, idx) => {
      positions[id] = {
        x: startX + idx * (nodeW + xGap),
        y: layer * (nodeH + yGap),
      };
    });
  });

  return positions;
}

const DAGCanvas: React.FC<{ nodes: DAGNode[]; edges: DAGEdge[] }> = ({ nodes, edges }) => {
  const positions = computeLayout(nodes, edges);

  const rfNodes: RFDAGNode[] = nodes.map(n => ({
    id: n.id,
    type: 'dagNode',
    position: positions[n.id] ?? { x: 0, y: 0 },
    data: { nodeType: n.type, label: n.label || n.type },
  }));

  const rfEdges: Edge[] = edges.map(e => ({
    id: e.id,
    source: e.source,
    target: e.target,
    label: e.label || (e.condition ? `${e.condition.field} ${e.condition.operator}` : undefined),
    markerEnd: { type: MarkerType.ArrowClosed },
  }));

  const [rn,, onRnC] = useNodesState<RFDAGNode>(rfNodes);
  const [re,, onReC] = useEdgesState(rfEdges);

  return (
    <ReactFlow
      nodes={rn}
      edges={re}
      nodeTypes={rfNodeTypes}
      onNodesChange={onRnC}
      onEdgesChange={onReC}
      fitView
      fitViewOptions={{ padding: 0.3 }}
      nodesDraggable={false}
      nodesConnectable={false}
    >
      <Background gap={16} size={1} color="#e5e7eb" />
      <Controls showInteractive={false} />
    </ReactFlow>
  );
};

// ----- component -------------------------------------------------------------

const WorkflowReadOnlyPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [definitions, setDefinitions] = useState<DAGDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await workflowService.listDAGDefinitions();
        setDefinitions(res.data);
      } catch {
        setError(t('workflows.readOnly.loadError', 'Failed to load workflows.'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [t]);

  return (
    <AppLayout pageTitle={t('workflows.readOnly.title', 'Workflows')}>
      <div className="h-full flex flex-col bg-main-bg">
        <MobileHeader
          title={t('workflows.readOnly.title', 'Workflows')}
          onBackClick={() => navigate('/settings')}
        />

        {/* Sub-header with back button */}
        <div className="hidden sm:block bg-white border-b border-gray-100 px-4 sm:px-6 py-4 flex-shrink-0">
          <div className="max-w-7xl mx-auto">
            <button
              onClick={() => navigate('/settings')}
              className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-2 transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
              {t('settings.backToSettings', 'Back to settings')}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-4 sm:px-6 py-6">
          <div className="max-w-5xl mx-auto">

            {/* Page title */}
            <div className="mb-6">
              <h1 className="text-xl font-semibold text-gray-900">
                {t('workflows.readOnly.title', 'Workflows')}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {t('workflows.readOnly.subtitle', 'Automations configured for your organisation')}
              </p>
            </div>

          {/* Content */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700">
              {error}
            </div>
          ) : definitions.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
              <GitMerge className="w-10 h-10 mx-auto mb-3 text-gray-200" />
              <p className="text-sm font-medium text-gray-500">
                {t('workflows.readOnly.noWorkflows', 'No workflows configured yet')}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {t('workflows.readOnly.noWorkflowsHint', 'Ask your admin to set up automation workflows.')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {definitions.map(def => {
                const actionSummary = (def.nodes ?? []).slice(0, 4).map(nodeLabel);
                const extraCount = Math.max(0, (def.nodes ?? []).length - 4);
                const isExpanded = expandedId === def.id;

                return (
                  <div
                    key={def.id}
                    className="bg-white rounded-2xl border border-gray-200 overflow-hidden"
                  >
                    {/* Card header */}
                    <button
                      className="w-full text-left px-5 py-4 flex items-start justify-between gap-3 hover:bg-gray-50 transition-colors"
                      onClick={() => setExpandedId(isExpanded ? null : def.id)}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm text-gray-900">{def.name}</span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                              def.is_active
                                ? 'bg-green-50 text-green-700 border border-green-200'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {def.is_active
                              ? t('workflows.readOnly.active', 'Active')
                              : t('workflows.readOnly.inactive', 'Inactive')}
                          </span>
                        </div>

                        {def.description && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate">{def.description}</p>
                        )}

                        {/* Trigger */}
                        <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-500">
                          <Zap className="w-3 h-3 text-amber-500 flex-shrink-0" />
                          <span className="font-medium">{triggerLabel(def.trigger?.type)}</span>
                        </div>

                        {/* Action pills */}
                        {actionSummary.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                            {actionSummary.map((a, i) => (
                              <React.Fragment key={i}>
                                {i > 0 && <ArrowRight className="w-2.5 h-2.5 text-gray-300 flex-shrink-0" />}
                                <span className="text-[10px] bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">{a}</span>
                              </React.Fragment>
                            ))}
                            {extraCount > 0 && (
                              <span className="text-[10px] text-gray-400">+{extraCount} more</span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex-shrink-0 pt-0.5 text-gray-400">
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </button>

                    {/* Expanded: DAG diagram */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-gray-50">
                        {(def.nodes ?? []).length === 0 ? (
                          <p className="px-5 py-4 text-xs text-gray-400">
                            {t('workflows.readOnly.noSteps', 'No steps defined.')}
                          </p>
                        ) : (
                          <div style={{ height: Math.max(320, (def.nodes ?? []).length * 80 + 160) }}>
                            <ReactFlowProvider>
                              <DAGCanvas nodes={def.nodes ?? []} edges={def.edges ?? []} />
                            </ReactFlowProvider>
                          </div>
                        )}

                        {/* Trigger filter details */}
                        {def.trigger?.filters && Object.keys(def.trigger.filters).length > 0 && (
                          <div className="px-5 pb-4 pt-2 border-t border-gray-200">
                            <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                              {t('workflows.readOnly.triggerFilters', 'Trigger filters')}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {Object.entries(def.trigger.filters).map(([k, v]) => (
                                <span
                                  key={k}
                                  className="text-[10px] bg-amber-50 border border-amber-200 text-amber-700 rounded-full px-2 py-0.5"
                                >
                                  {formatTriggerFilter(k, v)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default WorkflowReadOnlyPage;
